// M004-F0744 — the focus contract of the workspace's dialogs (receipt drawer, details drawer, delete confirm): on open, focus moves
// into the dialog; Tab and Shift+Tab stay inside it; Escape closes it; on close, focus returns to what opened it (the tile, the
// button). One implementation so every dialog on this page behaves identically (Q4 keyboard gate).
import { useEffect, useRef } from 'react';

// Dialogs can nest (the delete confirm opens over the details drawer): only the topmost one reacts to Escape and Tab.
const stack = [];

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function useDialogFocus(containerRef, onClose, { initialFocus = null } = {}) {
  const opener = useRef(typeof document !== 'undefined' ? document.activeElement : null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const token = {};
    stack.push(token);
    const container = containerRef.current;
    if (container) {
      const target = (initialFocus && container.querySelector(initialFocus)) || container.querySelector(FOCUSABLE) || container;
      if (target && typeof target.focus === 'function') target.focus();
    }
    const returnTo = opener.current;
    const onKey = (event) => {
      if (stack[stack.length - 1] !== token) return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (typeof closeRef.current === 'function') closeRef.current();
        return;
      }
      if (event.key !== 'Tab' || !containerRef.current) return;
      const nodes = Array.from(containerRef.current.querySelectorAll(FOCUSABLE)).filter((node) => node.offsetParent !== null || node === document.activeElement);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && (document.activeElement === first || !containerRef.current.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !containerRef.current.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      stack.splice(stack.indexOf(token), 1);
      if (returnTo && typeof returnTo.focus === 'function' && document.contains(returnTo)) returnTo.focus();
    };
  }, []);
}
